import React from 'react';
// Native MUI Components
import { 
  Card, 
  CardContent, 
  CardHeader, 
  Typography, 
  Chip, 
  IconButton, 
  Menu, 
  MenuItem,
  Box,
  Stack
} from '@mui/material';
import { 
  Place as MapPin, 
  MoreVert as MoreVertical, 
  CalendarMonth as CalendarDays, 
  People as Users, 
  Home, 
  ConfirmationNumber as Ticket, 
  CheckCircle as CheckCircle2, 
  Cancel as XCircle 
} from '@mui/icons-material';
import { Trip } from '@/types';
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/contexts/AuthContext';
import { calculateTripDuration } from '@/utils/calculations';

interface TripCardProps {
    trip: Trip;
    onSelectTrip: (tripId: string) => void;
    onDuplicateTrip?: (trip: Trip) => void;
    onToggleSignUp?: (trip: Trip) => void;
}

const TripCard: React.FC<TripCardProps> = ({ trip, onSelectTrip, onDuplicateTrip, onToggleSignUp }) => {
    const { t, formatDate } = useI18n();
    const { isAdmin } = useAuth();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleDuplicate = () => {
        if (onDuplicateTrip) {
            onDuplicateTrip(trip);
        }
        handleMenuClose();
    };

    const handleToggleSignUp = () => {
        if (onToggleSignUp) {
            onToggleSignUp(trip);
        }
        handleMenuClose();
    };

    return (
        <Card
            sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'space-between',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6
                }
            }}
            onClick={() => onSelectTrip(trip.id)}
        >
            <CardHeader
                title={
                    <Typography variant="h5">
                        {trip.name}
                    </Typography>
                }
                action={
                    <Stack direction="row" spacing={1} alignItems="center">
                        {trip.isSignUpOpen ? (
                            <Chip 
                                icon={<CheckCircle2 />}
                                label={t('trip.signUpOpen')}
                                size="small"
                                color='success'
                                sx={{ 
                                    color: 'white',
                                    '& .MuiChip-icon': {
                                        color: 'white'
                                    }
                                }}
                            />
                        ) : (
                            <Chip 
                                icon={<XCircle />}
                                label={t('trip.signUpClosed')}
                                size="small"
                                color='error'
                                sx={{ 
                                    color: 'white',
                                    '& .MuiChip-icon': {
                                        color: 'white'
                                    }
                                }}
                            />
                        )}
                        {isAdmin && onDuplicateTrip && onToggleSignUp && (
                            <>
                                <IconButton 
                                    size="small" 
                                    onClick={handleMenuClick}
                                >
                                    <MoreVertical />
                                </IconButton>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={handleMenuClose}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <MenuItem onClick={handleDuplicate}>
                                        {t('trip.duplicate')}
                                    </MenuItem>
                                    <MenuItem onClick={handleToggleSignUp}>
                                        {trip.isSignUpOpen ? t('trip.closeSignUp') : t('trip.openSignUp')}
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </Stack>
                }
                subheader={
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, mt: 1 }}>
                        <MapPin sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary', flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {trip.destination}
                        </Typography>
                    </Box>
                }
            />
            <CardContent sx={{ flexGrow: 1 }}>
                <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CalendarDays sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                                {formatDate(trip.startDate)} - {formatDate(trip.endDate)}
                            </Typography>
                        </Box>
                        <Chip 
                            label={t('trip.days', { count: calculateTripDuration(trip) })} 
                            size="small"
                            sx={{ 
                                bgcolor: 'primary.main', 
                                color: 'white',
                                fontWeight: 500,
                            }}
                        />
                    </Box>
                    {isAdmin && (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Users sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                    {t('trip.travellers', { count: trip.statistics.travellers.total })}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Home sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                    {t('trip.apartments', { count: trip.statistics.apartments.total })}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Ticket sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                    {t('trip.skiPasses', { count: trip.skiPasses.length })}
                                </Typography>
                            </Box>
                        </>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
};

export default TripCard;
